import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, forwardRef, useImperativeHandle, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { PromptTemplateDropdown } from '@/Domain/components/KnowledgeBase/PromptTemplateDropdown';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { useUpdateAssistantList } from '@/Domain/hooks/AI/useAssistantList.hook';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';
import { MAIN_PROMPT_LIMIT_FALLBACK } from '@/UI/Identity/Identity.constants';

type IdentityFormValues = Pick<KnowledgeBase, 'one_word_description' | 'short_description'>;

export interface IdentityStepHandle {
  validate: () => Promise<boolean>;
}

export const IdentityStep = forwardRef<IdentityStepHandle>((_props, ref) => {
  const { t } = useTranslation(['getting_started', 'knowledge_base']);
  const queryClient = useQueryClient();
  const { scenario } = useParams<{ scenario: string }>();
  const { updateAssistant } = useUpdateAssistantList();
  const { getConfig } = useSubscription();

  const { data: kb } = useQuery<KnowledgeBase>({ queryKey: ['knowledge_base'] });
  const mainPromptLimit =
    (getConfig('main_prompt_limit') as number | undefined) ?? MAIN_PROMPT_LIMIT_FALLBACK;

  // `values` + keepDirtyValues syncs server cache without clobbering in-flight edits during autosave round-trips.
  const form = useForm<IdentityFormValues>({
    values: {
      one_word_description: kb?.one_word_description ?? '',
      short_description: kb?.short_description ?? '',
    },
    resetOptions: { keepDirtyValues: true },
  });

  // Manual error state: fields light up only on explicit Continue, never on blur/submit churn.
  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  const mutation = useMutation<KnowledgeBase, Error, Partial<IdentityFormValues>>({
    mutationKey: ['knowledge_base'],
    onMutate: (updated) => {
      const previous = queryClient.getQueryData<KnowledgeBase>(['knowledge_base']);
      queryClient.setQueryData<KnowledgeBase>(['knowledge_base'], (prev) =>
        prev ? { ...prev, ...updated } : prev,
      );
      return { previous };
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['knowledge_base'], data);
      // Sidebar AI switcher cache stays stale until refresh otherwise.
      if (variables.one_word_description !== undefined && scenario) {
        updateAssistant(scenario, { name: data.one_word_description });
      }
    },
    onError: (_err, _vars, context) => {
      const ctx = context as { previous?: KnowledgeBase } | undefined;
      if (ctx?.previous) {
        queryClient.setQueryData(['knowledge_base'], ctx.previous);
      }
    },
  });

  const autoSubmit = useAutoSubmit(form, (data) => {
    if (Object.keys(data).length > 0) {
      mutation.mutate(data);
    }
  });

  useImperativeHandle(
    ref,
    () => ({
      validate: async () => {
        // Flush pending autosave first, or a fast typist hitting Continue mid-debounce loses the last chars.
        autoSubmit.flush();
        const { one_word_description, short_description } = form.getValues();
        const nameValid = (one_word_description ?? '').trim().length > 0;
        const descriptionValid = (short_description ?? '').trim().length > 0;
        setNameError(!nameValid);
        setDescriptionError(!descriptionValid);
        return nameValid && descriptionValid;
      },
    }),
    [form, autoSubmit],
  );

  const applyTemplate = (templateText: string) => {
    form.setValue('short_description', templateText, { shouldDirty: true });
    setDescriptionError(false);
  };

  if (!kb) return null;

  return (
    <FieldGroup data-testid="gs-identity-step">
      <Field>
        <FieldLabel htmlFor="gs-identity-name" className="font-medium text-foreground">
          {t('identity_step.name.label')}
        </FieldLabel>
        <Controller
          name="one_word_description"
          control={form.control}
          render={({ field }) => (
            <Input
              {...field}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                field.onChange(e);
                if (e.target.value.trim().length > 0) setNameError(false);
              }}
              id="gs-identity-name"
              type="text"
              placeholder={t('form.name.placeholder', { ns: 'knowledge_base' })}
              maxLength={40}
              autoCorrect="off"
              autoComplete="off"
              data-lpignore="true"
              data-form-type="other"
              data-testid="gs-identity-name-input"
              error={nameError}
            />
          )}
        />
        {nameError && (
          <FieldError data-testid="gs-identity-name-error">
            {t('identity_step.name.error')}
          </FieldError>
        )}
      </Field>
      <Field>
        <FieldLabel htmlFor="gs-identity-description" className="font-medium text-foreground">
          {t('identity_step.description.label')}
        </FieldLabel>
        {/* pb-12 reserves space below the textarea for the overlaid Templates trigger. */}
        <div className="relative">
          <Controller
            name="short_description"
            control={form.control}
            render={({ field }) => (
              <AutosizeTextarea
                {...field}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  field.onChange(e);
                  if (e.target.value.trim().length > 0) setDescriptionError(false);
                }}
                id="gs-identity-description"
                placeholder={t('form.description.placeholder', { ns: 'knowledge_base' })}
                className="px-4 pt-3 pb-12 rounded-xl"
                maxLength={mainPromptLimit}
                minHeight={140}
                maxHeight={300}
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
                data-testid="gs-identity-description-textarea"
                error={descriptionError}
              />
            )}
          />
          <PromptTemplateDropdown
            hasExistingValue={(form.watch('short_description') || '').trim().length > 0}
            onApply={applyTemplate}
            triggerClassName="absolute bottom-2 left-2"
            dropdownAlign="start"
            data-testid="gs-identity-templates"
          />
        </div>
        {descriptionError && (
          <FieldError data-testid="gs-identity-description-error">
            {t('identity_step.description.error')}
          </FieldError>
        )}
      </Field>
    </FieldGroup>
  );
});

IdentityStep.displayName = 'IdentityStep';
